import { useNavigate } from "@tanstack/react-router";
import { Download, Eye, MessageSquare, Star } from "lucide-react";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import { RiskBadge } from "@/components/skills/RiskBadge";
import { Stars } from "@/components/skills/Stars";
import { StatCard } from "@/components/skills/StatCard";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAnalytics } from "@/hooks/use-analytics";
import type { PerformanceRow } from "@/lib/skills/analytics";
import { analyticsToCsv } from "@/lib/skills/analytics-csv";
import { DownloadsByCategoryBars } from "./DownloadsByCategoryBars";
import { DownloadsBySkillChart } from "./DownloadsBySkillChart";

function downloadCsv(performance: PerformanceRow[]) {
  const blob = new Blob([analyticsToCsv(performance)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "markyt-analytics.csv";
  a.click();
  URL.revokeObjectURL(url);
  toast.success("Exported analytics CSV");
}

export function AnalyticsDashboard() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useAnalytics();

  if (isLoading) {
    return <div className="h-64 animate-pulse rounded-[12px] bg-bg-2" />;
  }
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Could not load analytics"}
        onRetry={() => void refetch()}
      />
    );
  }
  if (!data) return null;

  const { totals, topByDownloads, byCategory, performance } = data;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-fg-link">Govern</div>
          <h1 className="font-display text-3xl font-bold text-navy">Analytics</h1>
          <p className="max-w-2xl text-sm text-fg-2">
            Understand which skills are valuable to the community — downloads, views, ratings, and
            reviews across the library.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => downloadCsv(performance)}
          disabled={performance.length === 0}
        >
          <Download className="size-4" />
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Download}
          label="Total downloads"
          value={totals.downloads.toLocaleString()}
          caption="all time"
        />
        <StatCard
          icon={Eye}
          label="Total views"
          value={`${(totals.views / 1000).toFixed(1)}k`}
          caption="all time"
        />
        <StatCard
          icon={Star}
          label="Avg rating"
          value={totals.avgRating.toFixed(1)}
          caption="library average"
        />
        <StatCard
          icon={MessageSquare}
          label="Reviews"
          value={String(totals.reviews)}
          caption={
            totals.reviewsThisMonth > 0
              ? `${totals.reviewsThisMonth} this month`
              : `${totals.publishedCount} skills live`
          }
          up={totals.reviewsThisMonth > 0}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.7fr_1fr]">
        <DownloadsBySkillChart bars={topByDownloads} />
        <DownloadsByCategoryBars splits={byCategory} />
      </div>

      <div className="overflow-hidden rounded-[12px] border border-border-1 bg-bg-1 shadow-xs">
        <div className="flex items-center justify-between border-b border-border-1 px-6 py-4">
          <h3 className="font-display text-[17px] font-semibold text-navy">Skill performance</h3>
          <span className="text-xs text-fg-3">{totals.publishedCount} published skills</span>
        </div>
        {performance.length === 0 ? (
          <p className="px-6 py-8 text-sm text-fg-3">No published skills yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Skill</TableHead>
                <TableHead className="text-right">Views</TableHead>
                <TableHead className="text-right">Downloads</TableHead>
                <TableHead className="text-right">Conv.</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead className="text-right">Reviews</TableHead>
                <TableHead>Risk</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {performance.map((row) => (
                <TableRow key={row.slug}>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => void navigate({ to: "/skill/$id", params: { id: row.slug } })}
                      className="font-medium text-navy hover:text-fg-link"
                    >
                      {row.title}
                    </button>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.views.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.downloads.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.conversion}%</TableCell>
                  <TableCell>
                    <Stars value={row.rating} count={row.reviews} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.reviews}</TableCell>
                  <TableCell>
                    <RiskBadge level={row.risk} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
